import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { locale, t } from '../i18n/lingua.js';

/**
 * I filtri della ricerca avanzata, uno alla volta.
 *
 * Nel v2 premi RICERCA e compaiono diciassette campi tutti insieme: la lista
 * si schiaccia a dieci righe, ne compili uno, e quello che hai impostato
 * sparisce appena cambi pagina. Qui i filtri sono gli stessi, ma si
 * aggiungono quando servono, restano visibili come pastiglie — così si vede a
 * colpo d'occhio che cosa stai guardando — e finiscono dentro una vista
 * salvata insieme al resto.
 */

export interface Opzione {
  id: string | number;
  label: string;
}

export interface DefinizioneFiltro {
  key: string;
  label: string;
  type: 'text' | 'list' | 'yesNo' | 'choice' | 'date';
  options?: string;
  values?: readonly { value: string; label: string }[];
  /**
   * Si possono scegliere più valori insieme, scritti nell'indirizzo separati
   * da virgola. Vale per lo stato degli ordini, dove la domanda vera è quasi
   * sempre su un gruppo di stati e non su uno solo.
   */
  multi?: boolean;
}

/** I valori di un filtro a scelta multipla, letti dalla forma `3,5,7`. */
export function valoriMultipli(valore: string | number | undefined): string[] {
  if (valore === undefined || valore === '') return [];
  return String(valore)
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

interface Props {
  definizioni: DefinizioneFiltro[];
  opzioni: Record<string, Opzione[]>;
  valori: Record<string, string | number | undefined>;
  onCambia: (chiave: string, valore: string | number | undefined) => void;
}

const SI_NO = [
  { value: 'yes', label: t('sì') },
  { value: 'no', label: t('no') },
];

/**
 * Da che parte si apre un menu: a destra del pulsante, come sempre, ma verso
 * sinistra quando a destra non ci sta. Con qualche filtro già messo il
 * «+ Filtro» arriva in fondo alla riga, e il menu finiva tagliato dal bordo
 * della finestra (Antonio, 26 ottobre 2026). Si misura prima che il menu si
 * veda, così non salta da una parte all'altra sotto gli occhi.
 */
function useVerso(aperto: boolean) {
  const menu = useRef<HTMLDivElement>(null);
  const [aSinistra, setASinistra] = useState(false);

  useLayoutEffect(() => {
    if (!aperto) {
      setASinistra(false);
      return;
    }
    const bordo = menu.current?.getBoundingClientRect().right ?? 0;
    // la larghezza utile, senza la barra di scorrimento
    if (bordo > document.documentElement.clientWidth - 8) setASinistra(true);
  }, [aperto]);

  const stile: CSSProperties = aSinistra ? { left: 'auto', right: 0 } : { left: 0, right: 'auto' };
  return { menu, stile };
}

/**
 * Il pannello di un filtro a scelta multipla.
 *
 * Resta aperto mentre si spuntano le voci — chiuderlo a ogni clic
 * costringerebbe a riaprirlo per ogni stato, che è esattamente ciò che la
 * scelta multipla serve a evitare — e si chiude cliccando fuori o con Esc.
 * L'elenco si aggiorna a ogni spunta, così si vede subito l'effetto.
 */
function PannelloMultiplo({
  definizione,
  scelte,
  valore,
  onCambia,
  onChiudi,
}: {
  definizione: DefinizioneFiltro;
  scelte: { value: string; label: string }[];
  valore: string | number | undefined;
  onCambia: (chiave: string, valore: string | number | undefined) => void;
  onChiudi: () => void;
}) {
  const contenitore = useRef<HTMLDivElement>(null);
  const verso = useVerso(true);

  useEffect(() => {
    const fuori = (e: MouseEvent) => {
      if (!contenitore.current?.contains(e.target as Node)) onChiudi();
    };
    const tasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChiudi();
    };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', tasto);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', tasto);
    };
  }, [onChiudi]);

  const scelti = valoriMultipli(valore);
  const cambia = (v: string) => {
    const nuovi = scelti.includes(v) ? scelti.filter((s) => s !== v) : [...scelti, v];
    onCambia(definizione.key, nuovi.length ? nuovi.join(',') : undefined);
  };

  return (
    <div className="menu-wrap" ref={contenitore}>
      <button className="chip chip-set" onClick={onChiudi}>
        {t(definizione.label)}{' '}
        <b>{scelti.length === 0 ? t('tutti') : t('{n} scelti', { n: scelti.length })}</b>
      </button>
      <div className="menu menu-multi" role="menu" ref={verso.menu} style={verso.stile}>
        {scelte.map((s) => (
          <label key={s.value} className="menu-item menu-check">
            <input
              type="checkbox"
              checked={scelti.includes(s.value)}
              onChange={() => cambia(s.value)}
            />
            <span>{s.label}</span>
          </label>
        ))}
        {scelti.length > 0 && (
          <button className="menu-item menu-azzera" onClick={() => onCambia(definizione.key, undefined)}>
            {t('Togli il filtro')}
          </button>
        )}
      </div>
    </div>
  );
}

export function BarraFiltri({ definizioni, opzioni, valori, onCambia }: Props) {
  const [menuAperto, setMenuAperto] = useState(false);
  const [inModifica, setInModifica] = useState<string | null>(null);
  const contenitore = useRef<HTMLDivElement>(null);
  const verso = useVerso(menuAperto);

  useEffect(() => {
    if (!menuAperto) return;
    const chiudi = (e: MouseEvent) => {
      if (!contenitore.current?.contains(e.target as Node)) setMenuAperto(false);
    };
    document.addEventListener('mousedown', chiudi);
    return () => document.removeEventListener('mousedown', chiudi);
  }, [menuAperto]);

  const attivi = definizioni.filter((d) => valori[d.key] !== undefined && valori[d.key] !== '');
  const disponibili = definizioni.filter((d) => !attivi.includes(d));

  const etichettaValore = (d: DefinizioneFiltro): string => {
    const valore = valori[d.key];
    if (valore === undefined) return '';
    if (d.type === 'list' && d.options) {
      const elenco = opzioni[d.options] ?? [];
      const nome = (v: string) => elenco.find((o) => String(o.id) === v)?.label ?? v;
      if (d.multi) {
        const scelti = valoriMultipli(valore);
        // due nomi si leggono, cinque no: oltre si dice quanti sono e basta
        return scelti.length <= 2
          ? scelti.map(nome).join(', ')
          : t('{n} scelti', { n: scelti.length });
      }
      return nome(String(valore));
    }
    if (d.type === 'yesNo') return valore === 'yes' ? t('sì') : t('no');
    // la data si scrive come la si legge qui, non come la vuole il servizio
    if (d.type === 'date') return new Date(String(valore)).toLocaleDateString(locale());
    if (d.type === 'choice') {
      const scelta = d.values?.find((v) => v.value === valore);
      return scelta ? t(scelta.label) : String(valore);
    }
    return String(valore);
  };

  const campo = (d: DefinizioneFiltro) => {
    const chiudiDopo = (valore: string | number | undefined) => {
      onCambia(d.key, valore);
      setInModifica(null);
    };

    if (d.type === 'text' || d.type === 'date') {
      return (
        <input
          className="input"
          type={d.type === 'date' ? 'date' : 'text'}
          style={{ height: 26, width: d.type === 'date' ? 140 : 160 }}
          autoFocus
          defaultValue={(valori[d.key] as string) ?? ''}
          placeholder={t(d.label)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') chiudiDopo((e.target as HTMLInputElement).value || undefined);
            if (e.key === 'Escape') setInModifica(null);
          }}
          onBlur={(e) => chiudiDopo(e.target.value || undefined)}
        />
      );
    }

    const scelte: { value: string; label: string }[] =
      d.type === 'yesNo'
        ? SI_NO
        : d.type === 'choice'
          ? (d.values ?? []).map((v) => ({ value: v.value, label: t(v.label) }))
          : (opzioni[d.options ?? ''] ?? []).map((o) => ({
              value: String(o.id),
              label: o.label,
            }));

    /**
     * A scelta multipla il menu resta aperto mentre si spuntano le voci:
     * chiuderlo a ogni clic costringerebbe a riaprirlo per ogni stato, che è
     * il motivo per cui si sceglie il multiplo.
     */
    if (d.multi) {
      return (
        <PannelloMultiplo
          definizione={d}
          scelte={scelte}
          valore={valori[d.key]}
          onCambia={onCambia}
          onChiudi={() => setInModifica(null)}
        />
      );
    }

    return (
      <select
        className="select"
        style={{ height: 26, width: 'auto', minWidth: 130 }}
        autoFocus
        defaultValue={String(valori[d.key] ?? '')}
        onChange={(e) => chiudiDopo(e.target.value || undefined)}
        onBlur={() => setInModifica(null)}
      >
        <option value="">{t(d.label)}…</option>
        {scelte.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
    );
  };

  return (
    <>
      {attivi.map((d) =>
        inModifica === d.key ? (
          <span key={d.key} className="row" style={{ gap: 4 }}>
            {campo(d)}
          </span>
        ) : (
          <button
            key={d.key}
            className="chip chip-set"
            onClick={() => setInModifica(d.key)}
            title={t('Cambia questo filtro')}
          >
            {t(d.label)} <b>{etichettaValore(d)}</b>
            <span
              className="chip-x"
              role="button"
              aria-label={t('Togli il filtro {nome}', { nome: t(d.label) })}
              onClick={(e) => {
                e.stopPropagation();
                onCambia(d.key, undefined);
              }}
            >
              ×
            </span>
          </button>
        ),
      )}

      {inModifica !== null && !attivi.some((d) => d.key === inModifica) && (
        <span className="row" style={{ gap: 4 }}>
          {campo(definizioni.find((d) => d.key === inModifica)!)}
        </span>
      )}

      {disponibili.length > 0 && (
        <div className="menu-wrap" ref={contenitore}>
          <button className="chip" onClick={() => setMenuAperto((v) => !v)} aria-haspopup="menu">
            {t('+ Filtro')}
          </button>
          {menuAperto && (
            <div
              className="menu"
              role="menu"
              ref={verso.menu}
              style={{ ...verso.stile, maxHeight: 320, overflowY: 'auto' }}
            >
              <div className="menu-label">{t('Aggiungi un filtro')}</div>
              {disponibili.map((d) => (
                <button
                  key={d.key}
                  className="menu-item"
                  onClick={() => {
                    setMenuAperto(false);
                    setInModifica(d.key);
                  }}
                >
                  {t(d.label)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
